import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  collection, doc, onSnapshot, setDoc, deleteDoc, serverTimestamp,
} from 'firebase/firestore';
import { db } from '../lib/firebase';

/**
 * useSharedListPlacements — subscribes to the current user's
 * `sharedListPlacements` subcollection.
 *
 * Walter 2026-05-11: the placement collection is the invitee-side
 * record of WHICH of their own boards a given shared list should
 * appear in. The list itself still lives at
 *   users/{sourceOwnerUid}/boards/{sourceBoardId}/lists/{listId}
 * (i.e. on the OWNER's side); the placement record just maps
 * listId → targetBoardId for the current user. Two invitees can
 * place the same shared list in different boards because each
 * has their own placement subcollection.
 *
 * Doc shape (see SHARING_SPEC.md "Per-invitee list placement"):
 *   users/{uid}/sharedListPlacements/{listId} = {
 *     sourceOwnerUid: string,   // owner of the list (path-anchor)
 *     sourceBoardId:  string,   // board the list lives in on the owner's side
 *     boardId:        string,   // which of the invitee's own boards this list lives on
 *     placedAt:       Timestamp,
 *   }
 *
 * Why the data lives here and not on the list doc:
 *   - Multiple invitees can each choose their own target board; the
 *     placement is per-user, so writing it to the shared list doc
 *     would conflict.
 *   - The list doc stays owner-owned (the owner controls who can
 *     edit it, what its content is, where IT lives in their own
 *     boards). Placement is an invitee-side organisation layer
 *     that never touches owner-side state.
 *
 * Returns:
 *   {
 *     placements,      // map listId → placement doc (with id field added)
 *     placementsByBoard, // map targetBoardId → array of placement docs
 *     loading,
 *     setPlacement,    // (listId, { sourceOwnerUid, sourceBoardId, boardId }) => Promise<void>
 *     clearPlacement,  // (listId) => Promise<void>
 *   }
 */
export function useSharedListPlacements(userId) {
  const [items, setItems] = useState([]);
  const [resolvedFor, setResolvedFor] = useState(undefined);
  const loading = userId !== resolvedFor;

  useEffect(() => {
    if (!userId) {
      setItems([]);
      setResolvedFor(null);
      return undefined;
    }
    const unsub = onSnapshot(
      collection(db, 'users', userId, 'sharedListPlacements'),
      (snap) => {
        const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        // Walter 2026-05-17: auto-clean self-referencing placements.
        // A placement whose `sourceOwnerUid` is the current user is
        // bogus by definition — you can't "share a list with
        // yourself". These leak from older code paths and silently
        // hid real own-lists in BoardPage's filter. Detect + delete
        // so the broken filter behavior can't recur even if the
        // filter were re-broken in the future.
        // Walter 2026-05-17: self-cleanup of bogus self-referencing
        // placement docs. Diagnostic verbosity removed once Walter's
        // missing-Done bug was traced to a cache desync (not a
        // placement issue) — cleanup itself stays as a safety net.
        for (const p of docs) {
          if (p.sourceOwnerUid && p.sourceOwnerUid === userId) {
            const ref = doc(db, 'users', userId, 'sharedListPlacements', p.id);
            deleteDoc(ref).catch(err => {
              console.warn('[useSharedListPlacements] self-cleanup failed', p.id, err);
            });
          }
        }
        setItems(docs);
        setResolvedFor(userId);
      },
      (err) => {
        // Non-fatal — placements just don't render. Most likely cause
        // would be rules denials, which shouldn't happen since the
        // user reads their own subtree.
        console.warn('[useSharedListPlacements] snapshot error', err);
        setItems([]);
        setResolvedFor(userId);
      },
    );
    return unsub;
  }, [userId]);

  // Map listId → placement. Lookups by listId are the common access
  // pattern (e.g. "is this shared list placed on the current board?").
  const placements = useMemo(() => {
    const m = {};
    for (const p of items) m[p.id] = p;
    return m;
  }, [items]);

  // Map targetBoardId → list of placement docs targeting that board.
  // Used by BoardPage to enumerate which shared lists to merge into
  // the current board's canvas.
  const placementsByBoard = useMemo(() => {
    const m = {};
    for (const p of items) {
      if (!p.boardId) continue;
      if (!m[p.boardId]) m[p.boardId] = [];
      m[p.boardId].push(p);
    }
    return m;
  }, [items]);

  const setPlacement = useCallback(async (listId, opts) => {
    if (!userId || !listId) return;
    const {
      sourceOwnerUid, sourceBoardId, boardId,
      sourceOwnerName, sourceCachedTitle,
      // Walter 2026-05-11: positional override fields. Per-invitee
      // x/y/w/h (free mode) + gridCol/gridRow (grid mode) + z let
      // the placed shared list sit at its own position on the
      // invitee's board without writing through to the owner's
      // list doc. The brief calls these out as the per-user
      // placement schema; persisted optionally so a v1 placement
      // can ship without them and the renderer falls back to the
      // owner's positional fields.
      x, y, w, h, z, gridCol, gridRow, order,
    } = opts || {};
    if (!sourceOwnerUid || !sourceBoardId || !boardId) {
      // Walter 2026-07-19 (placement bug): this guard used to DROP the write
      // silently (console.warn only) — the user picked a board, saw no error,
      // and the list stayed in "shared with me" as an unplaced orphan. Now it
      // THROWS: every placement picker (BoardPickerModal callers) already
      // catches and shows the message inline, so a bad invite payload is
      // visible the moment it happens instead of failing invisibly.
      const missing = [
        !sourceOwnerUid && 'sourceOwnerUid',
        !sourceBoardId && 'sourceBoardId',
        !boardId && 'boardId',
      ].filter(Boolean).join(', ');
      console.warn('[useSharedListPlacements.setPlacement] missing fields', opts);
      throw new Error(`couldn't place the list — the share is missing ${missing}. Try again from the list's ⋯ menu.`);
    }
    // sourceOwnerName + sourceCachedTitle are best-effort caches:
    // the caller passes whatever they know at placement time
    // (inviteInfo.fromName / inviteInfo.targetTitle for accept-flow
    // placements; cachedOwnerName + the list's live title for
    // ⋯-menu placements). Empty strings are fine — UI falls back
    // gracefully.
    //
    // Walter 2026-05-11: sourceCachedTitle was added because a
    // placed shared list whose owner left the title blank rendered
    // as "(empty)" on the invitee's board — visually identical to
    // their own untitled lists. Caching the title at place-time
    // gives the invitee something stable to read, and the panel
    // also falls back to it when the live list's title is empty.
    const payload = {
      sourceOwnerUid,
      sourceBoardId,
      boardId,
      placedAt: serverTimestamp(),
    };
    if (typeof sourceOwnerName === 'string' && sourceOwnerName.length > 0) {
      payload.sourceOwnerName = sourceOwnerName;
    }
    if (typeof sourceCachedTitle === 'string' && sourceCachedTitle.length > 0) {
      payload.sourceCachedTitle = sourceCachedTitle;
    }
    if (typeof x === 'number' && Number.isFinite(x)) payload.x = x;
    if (typeof y === 'number' && Number.isFinite(y)) payload.y = y;
    if (typeof w === 'number' && Number.isFinite(w)) payload.w = w;
    if (typeof h === 'number' && Number.isFinite(h)) payload.h = h;
    if (typeof z === 'number' && Number.isFinite(z)) payload.z = z;
    if (typeof gridCol === 'number' && Number.isFinite(gridCol)) payload.gridCol = gridCol;
    if (typeof gridRow === 'number' && Number.isFinite(gridRow)) payload.gridRow = gridRow;
    if (typeof order === 'number' && Number.isFinite(order)) payload.order = order;
    await setDoc(
      doc(db, 'users', userId, 'sharedListPlacements', listId),
      payload,
      { merge: true },
    );
  }, [userId]);

  const clearPlacement = useCallback(async (listId) => {
    if (!userId || !listId) return;
    await deleteDoc(doc(db, 'users', userId, 'sharedListPlacements', listId));
  }, [userId]);

  return { placements, placementsByBoard, loading, setPlacement, clearPlacement };
}
